import { useState } from 'react';
import { 
  Box, 
  Button, 
  Flex, 
  FormControl, 
  FormErrorMessage, 
  Input, 
  Text, 
  useToast,
  VStack,
  useColorModeValue
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { subscribeToNewsletter } from '../../services/public';
import { trackNewsletterSubscribe, trackFormSubmit } from '../../utils/umami';
import { useTranslation } from 'react-i18next';

type FormData = {
  email: string;
};

export default function NewsletterSubscribe() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await subscribeToNewsletter(data.email);
      
      // Track successful newsletter subscription
      trackNewsletterSubscribe(window.location.pathname);
      trackFormSubmit('Newsletter Subscribe', true);
      
      toast({
        title: t('newsletter.subscribe_success'),
        description: t('newsletter.subscribe_success_desc'),
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      try { window.dispatchEvent(new CustomEvent('engagement:refresh')); } catch {}
      reset();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('newsletter.subscribe_error_desc');
      
      // Track failed subscription
      trackFormSubmit('Newsletter Subscribe', false);
      
      toast({
        title: t('newsletter.subscribe_error'),
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const disclaimerColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box w="100%" maxW="xl" mx="auto" p={4}>
      <VStack spacing={3} align="stretch">
        <Text fontSize="xl" fontWeight="bold" textAlign="center" color={headingColor}>
          {t('newsletter.subscribe')}
        </Text>
        <Text textAlign="center" color={textColor} mb={2}>
          {t('newsletter.subscribe_description')}
        </Text>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={3}>
            <FormControl isInvalid={!!errors.email}>
              <Input
                id="email"
                type="email"
                placeholder={t('newsletter.email_label')}
                autoComplete="email"
                {...register('email', {
                  required: t('newsletter.email_required'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('newsletter.email_invalid'),
                  },
                })}
                size="md"
                disabled={isLoading}
              />
              <FormErrorMessage>
                {errors.email && errors.email.message}
              </FormErrorMessage>
            </FormControl>
            
            <Button
              type="submit"
              colorScheme="blue"
              size="md"
              width="100%"
              isLoading={isLoading}
              loadingText={t('newsletter.loading')}
              data-umami-event="Newsletter Submit"
              data-umami-event-location={window.location.pathname}
            >
              {t('newsletter.subscribe_button')}
            </Button>
          </VStack>
        </form>
        
        <Text fontSize="xs" color={disclaimerColor} textAlign="center" mt={2}>
          {t('newsletter.consent_text')}
        </Text>
      </VStack>
    </Box>
  );
}
